import { apiClient } from './api';
import type {
  Asset,
  AssetListeningPort,
  AssetNetworkInterface,
  BackendAssetListResponse,
  BackendAssetListeningPort,
  BackendAssetNetworkInterface,
  BackendAssetResponse,
  CreateAssetDTO,
  ListAssetsParams,
  UpdateAssetDTO,
} from '@/types/assets';

function toDate(value?: string | null) {
  return value ? new Date(value) : undefined;
}

function toNetworkInterface(
  ni: BackendAssetNetworkInterface,
): AssetNetworkInterface {
  return {
    interface: ni.interface,
    address: ni.address,
    mask: ni.mask ?? undefined,
    type: ni.type ?? undefined,
    mac: ni.mac ?? undefined,
    description: ni.description ?? undefined,
    last_reported_at: toDate(ni.last_reported_at),
  };
}

function toListeningPort(lp: BackendAssetListeningPort): AssetListeningPort {
  return {
    port: lp.port,
    protocol: lp.protocol,
    address: lp.address,
    process_name: lp.process_name ?? undefined,
    process_path: lp.process_path ?? undefined,
    pid: lp.pid ?? undefined,
    last_reported_at: toDate(lp.last_reported_at),
  };
}

function toAsset(data: BackendAssetResponse): Asset {
  return {
    ...data,
    organization_id: data.organization_id,
    owner_sub: data.owner_sub ?? undefined,
    location: data.location ?? undefined,
    serial_number: data.serial_number ?? undefined,
    mac_address: data.mac_address ?? undefined,
    ip_address: data.ip_address ?? undefined,
    hostname: data.hostname ?? undefined,
    manufacturer: data.manufacturer ?? undefined,
    model: data.model ?? undefined,
    network_segment: data.network_segment ?? undefined,
    network_zone: (data.network_zone as Asset['network_zone']) ?? undefined,
    os_name: data.os_name ?? undefined,
    os_version: data.os_version ?? undefined,
    platform: data.platform ?? undefined,
    cpu_brand: data.cpu_brand ?? undefined,
    cpu_logical_cores: data.cpu_logical_cores ?? undefined,
    total_memory_bytes: data.total_memory_bytes ?? undefined,
    disk_total_gb: data.disk_total_gb ?? undefined,
    disk_used_gb: data.disk_used_gb ?? undefined,
    disk_free_gb: data.disk_free_gb ?? undefined,
    antivirus_installed: data.antivirus_installed ?? undefined,
    patch_status: data.patch_status ?? undefined,
    last_patch_date: toDate(data.last_patch_date),
    last_scan_date: toDate(data.last_scan_date),
    vulnerability_score: data.vulnerability_score ?? undefined,
    purchase_date: toDate(data.purchase_date),
    warranty_expiry: toDate(data.warranty_expiry),
    deployment_date: toDate(data.deployment_date),
    eol_date: toDate(data.eol_date),
    eos_date: toDate(data.eos_date),
    last_backup_date: toDate(data.last_backup_date),
    last_seen_at: toDate(data.last_seen_at),
    network_interfaces: data.network_interfaces?.map(toNetworkInterface),
    listening_ports: data.listening_ports?.map(toListeningPort),
    created_at: new Date(data.created_at),
    updated_at: toDate(data.updated_at),
    asset_type: data.asset_type as Asset['asset_type'],
    status: data.status as Asset['status'],
    criticality_level: data.criticality_level as Asset['criticality_level'],
  };
}

export interface AssetDashboardOverview {
  /** Whole inventory; the date filter does not narrow it. */
  total: number;
  /** Assets registered within the date filter; null when none was sent. */
  newInWindow: number | null;
  byCriticality: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  /**
   * Open/acknowledged critical and high findings — the alerts each asset's
   * Alerts tab lists — raised within the date filter.
   */
  alerts: {
    total: number;
    critical: number;
    high: number;
  };
}

export const assetService = {
  async list(params?: ListAssetsParams) {
    const response = await apiClient.get<BackendAssetListResponse>('/asset/assets', {
      params,
    });
    return {
      data: response.data.map(toAsset),
      page: response.page,
      pageSize: response.pageSize,
      total: response.total,
    };
  },

  /**
   * Total and per-criticality asset counts in one request.
   *
   * Replaces the five `pageSize: 1` list calls the dashboard used to make just
   * to read their `total`.
   */
  async dashboardOverview(
    params: { dateRange?: string; date_from?: string; date_to?: string } = {},
  ): Promise<AssetDashboardOverview> {
    const response = await apiClient.get<AssetDashboardOverview>(
      '/asset/assets/dashboard/overview',
      { params },
    );
    const byCriticality = response?.byCriticality;
    return {
      total: Number(response?.total ?? 0),
      newInWindow: response?.newInWindow == null ? null : Number(response.newInWindow),
      byCriticality: {
        critical: Number(byCriticality?.critical ?? 0),
        high: Number(byCriticality?.high ?? 0),
        medium: Number(byCriticality?.medium ?? 0),
        low: Number(byCriticality?.low ?? 0),
      },
      alerts: {
        total: Number(response?.alerts?.total ?? 0),
        critical: Number(response?.alerts?.critical ?? 0),
        high: Number(response?.alerts?.high ?? 0),
      },
    };
  },

  async get(id: string) {
    const response = await apiClient.get<BackendAssetResponse>(`/asset/assets/${id}`);
    return toAsset(response);
  },

  async getDetail(id: string) {
    const response = await apiClient.get<BackendAssetResponse>(`/asset/assets/${id}`);
    return toAsset(response);
  },

  async create(data: CreateAssetDTO) {
    const response = await apiClient.post<BackendAssetResponse>('/asset/assets', data);
    return toAsset(response);
  },

  async update(id: string, data: UpdateAssetDTO) {
    const response = await apiClient.patch<BackendAssetResponse>(`/asset/assets/${id}`, data);
    return toAsset(response);
  },

  async delete(id: string) {
    await apiClient.delete<{ deleted?: boolean }>(`/asset/assets/${id}`);
    return { success: true };
  },
};
